import { loadMission } from "./loader";
import type { MissionConfig } from "./types";
import type { NodeKind, NodeModifier, RunNode, RunTheater } from "../meta/run-types";
import riverValleyData from "../../missions/river-valley-01.json";
import ironMarketData from "../../missions/iron-market-01.json";
import greenHammerData from "../../missions/green-hammer-01.json";
import ironCurtainData from "../../missions/iron-curtain-01.json";
import arcticWolfData from "../../missions/arctic-wolf-01.json";

// Run-node missions are variants sliced out of the authored river-valley
// route: same world, same anchors, different segment, scaled pressure. The
// transformation happens on raw mission JSON and the result goes back
// through loadMission, so variants are validated exactly like authored
// missions (validate at the boundary).

export type VariantSpec = {
  key: string;
  name: string;
  routeFrom: number; // inclusive waypoint index into the base route
  routeTo: number; // inclusive
  briefing: string[];
  blurb: string; // one-liner for the run map screen
  // Anti-air sites near this segment (filtered by the same keep radius as
  // anchors). Elites field an extra MANPADS team.
  flakSites?: Array<{ kind: "aa" | "manpads"; origin: [number, number, number] }>;
  // Armor near this segment (same keep-radius filter). Elites and the
  // finale add a tank regardless.
  armorUnits?: Array<{ kind: "tank" | "truck"; origin: [number, number, number] }>;
};

// Indices reference missions/river-valley-01.json squadRoute:
// 0 BASE GATE, 1 VALLEY ENTRY, 2 RIVER TRACK, 3 BRIDGE OVERWATCH,
// 4 LOW TOWN, 5 EXFIL LOT.
export const VALLEY_VARIANTS: VariantSpec[] = [
  {
    key: "valley-entry",
    name: "VALLEY ENTRY",
    routeFrom: 0,
    routeTo: 2,
    blurb: "Escort Alpha out of the wire, past the ridge ambush to the river track.",
    briefing: [
      "Alpha steps off from FOB KESTREL. Short push: gate, valley mouth, river track.",
      "Ridgeline ambush expected at the valley entry. Sweep high ground before they commit.",
      "S-2 flags a gun position on the ridge — stay high or kill it early.",
    ],
    flakSites: [{ kind: "aa", origin: [-360, 0, -300] }],
  },
  {
    key: "bridge-fork",
    name: "BRIDGE FORK",
    routeFrom: 2,
    routeTo: 4,
    blurb: "Push the river track past the hamlet and take the bridge fork.",
    briefing: [
      "Alpha holds the river track. Objective: move up past the hamlet and secure the bridge fork.",
      "A nest covers the far bank approach. The river is impassable — the bridge is the only crossing.",
      "MANPADS reported with the bridge garrison. Keep flares warm.",
    ],
    flakSites: [{ kind: "manpads", origin: [-160, 0, 55] }],
    armorUnits: [{ kind: "truck", origin: [-70, 0, 120] }],
  },
  {
    key: "town-push",
    name: "TOWN PUSH",
    routeFrom: 3,
    routeTo: 5,
    blurb: "Clear the low town and get Alpha to the hardstand.",
    briefing: [
      "From the bridge overwatch, Alpha pushes into the low town. It's tight — danger close is real.",
      "Vehicles stage south of the lot. Break them before they reach the squad.",
      "AA gun on the town edge. It owns the low air — respect it or erase it.",
    ],
    flakSites: [{ kind: "aa", origin: [-115, 0, 265] }],
    armorUnits: [{ kind: "truck", origin: [-40, 0, 330] }],
  },
  {
    key: "full-exfil",
    name: "RIVER VALLEY EXFIL",
    routeFrom: 0,
    routeTo: 5,
    blurb: "The full valley: every phase, every gun, and the LZ hold at the end.",
    briefing: [
      "This is the one. Full route: checkpoint, hamlet, bridge fork, low town, LZ hold.",
      "Everything the valley has is awake — guns on the ridge and the town, MANPADS at the bridge.",
      "Hold the lot until the bird is inbound. Bring Specter home too.",
    ],
    flakSites: [
      { kind: "aa", origin: [-360, 0, -300] },
      { kind: "manpads", origin: [-160, 0, 55] },
      { kind: "aa", origin: [-115, 0, 265] },
    ],
    armorUnits: [
      { kind: "truck", origin: [-70, 0, 120] },
      { kind: "tank", origin: [-30, 0, 440] },
    ],
  },
];

type Vec3 = [number, number, number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function distToPolyline(origin: Vec3, waypoints: Vec3[]): number {
  let best = Infinity;
  for (const wp of waypoints) {
    best = Math.min(best, Math.hypot(origin[0] - wp[0], origin[2] - wp[2]));
  }
  return best;
}

// An anchor stays in a variant only if the squad will actually meet it.
const ANCHOR_KEEP_RADIUS = 380;

export type VariantScaling = {
  countMult: number;
  budgetDelta: number; // from run mods (interdiction); applied after scaling
  squadSize: number;
  minSurvivors: number;
};

export function buildVariantMission(
  spec: VariantSpec,
  kind: NodeKind,
  scaling: VariantScaling,
  baseJson: unknown = riverValleyData,
): MissionConfig {
  if (!isRecord(baseJson)) throw new Error("variant base mission JSON must be an object");
  const base = structuredClone(baseJson) as Record<string, unknown>;

  const route = Array.isArray(base.squadRoute) ? (base.squadRoute as Record<string, unknown>[]) : [];
  if (spec.routeTo >= route.length || spec.routeFrom < 0 || spec.routeFrom >= spec.routeTo) {
    throw new Error(`variant ${spec.key}: route slice ${spec.routeFrom}..${spec.routeTo} out of range`);
  }

  const sliced = route.slice(spec.routeFrom, spec.routeTo + 1);
  // Non-final variants end on arrival, not on a timed hold — the LZ hold is
  // the finale's signature and shouldn't leak into mid-run nodes.
  if (kind !== "final") {
    const last = { ...sliced[sliced.length - 1]! };
    delete last.holdTime;
    delete last.holdUntilClear;
    sliced[sliced.length - 1] = last;
  }
  const routePositions = sliced.map((n) => n.position as Vec3);

  const anchors = Array.isArray(base.enemyAnchors) ? (base.enemyAnchors as Record<string, unknown>[]) : [];
  const kept: Record<string, unknown>[] = [];
  for (const anchor of anchors) {
    const a = { ...anchor };
    const origin = a.origin as Vec3;
    if (distToPolyline(origin, routePositions) > ANCHOR_KEEP_RADIUS) continue;

    if (a.trigger === "waypointReached") {
      const idx = a.waypointIndex as number;
      if (idx >= spec.routeTo) continue; // fires after the sliced route ends
      if (idx < spec.routeFrom) {
        // Garrison sitting before the slice: present from the start
        a.trigger = "immediate";
        delete a.waypointIndex;
      } else {
        a.waypointIndex = idx - spec.routeFrom;
      }
    }

    a.count = Math.max(1, Math.round((a.count as number) * scaling.countMult));
    kept.push(a);
  }

  const baseBudget = typeof base.reinforcementBudget === "number" ? base.reinforcementBudget : 10;
  const budget = Math.max(0, Math.round(baseBudget * scaling.countMult) + scaling.budgetDelta);

  // Anti-air near the segment; elites field an extra MANPADS team at the
  // route's far end.
  const flakSites = (spec.flakSites ?? []).filter(
    (site) => distToPolyline(site.origin, routePositions) <= ANCHOR_KEEP_RADIUS,
  );
  const armorUnits = (spec.armorUnits ?? []).filter(
    (unit) => distToPolyline(unit.origin, routePositions) <= ANCHOR_KEEP_RADIUS,
  );
  if (kind === "elite" || kind === "final") {
    const last = routePositions[routePositions.length - 1]!;
    flakSites.push({ kind: "manpads", origin: [last[0] + 90, 0, last[2] - 60] });
    // Hardened resistance brings armor to the far end of the segment
    armorUnits.push({ kind: "tank", origin: [last[0] - 110, 0, last[2] + 70] });
  }

  const eliteTag = kind === "elite" ? " — HARDENED" : "";
  const transformed: Record<string, unknown> = {
    ...base,
    id: `run-${spec.key}${kind === "elite" ? "-elite" : ""}`,
    name: `${spec.name}${eliteTag}`,
    squadRoute: sliced,
    enemyAnchors: kept,
    reinforcementBudget: budget,
    briefing: spec.briefing,
    winObjectives: [{ type: "SquadReachDestination", label: `REACH ${(sliced[sliced.length - 1]!.label as string) ?? "OBJECTIVE"}` }],
    failObjectives: [{ type: "SquadSurvival", label: `KEEP ${scaling.minSurvivors}+ ALIVE`, minSurvivors: scaling.minSurvivors }],
  };
  // Waves are the legacy spawn path; run variants speak anchors only.
  delete transformed.waves;

  const config = loadMission(transformed);
  config.squadSize = scaling.squadSize;
  // Flak/armor are engine-side config, not mission-JSON content — attach
  // post-load.
  if (flakSites.length > 0) config.flakSites = flakSites;
  if (armorUnits.length > 0) config.armorUnits = armorUnits;
  return config;
}

// Difficulty curve by run column; elites spike on top.
export function scalingForNode(node: RunNode, budgetDelta: number, squadSize: number): VariantScaling {
  const depthMult = 1 + node.col * 0.22;
  const eliteMult = node.kind === "elite" ? 1.45 : 1;
  return {
    countMult: depthMult * eliteMult,
    budgetDelta,
    squadSize,
    minSurvivors: 1,
  };
}

export function variantForNode(node: RunNode, theater: TheaterDef = THEATERS["stone-wall"]): VariantSpec {
  const variants = theater.variants;
  if (node.kind === "final") return variants[variants.length - 1]!;
  const combatVariants = variants.length - 1;
  return variants[node.variant % combatVariants]!;
}

// ── Non-escort mission types (docs/design/MISSION_VARIETY.md) ──────────────
// DEFEND holds a site against converging waves; DESTROY and AMBUSH are
// squad-less fire missions. All are built as raw mission JSON and re-validated
// through loadMission like the escort variants.

type DefendSite = {
  key: string;
  name: string;
  position: Vec3;
  blurb: string;
  briefing: string[];
};

const DEFEND_SITES: DefendSite[] = [
  {
    key: "hamlet-hold",
    name: "HAMLET DEFENSE",
    position: [-450, 0, -95],
    blurb: "Alpha digs in at the river track. Waves converge from three sides.",
    briefing: [
      "Alpha is static at the river track hamlet. They cannot move — you cover all angles.",
      "REDFOR converges from the ridge, the river bend, and the south track. Watch your sectors.",
    ],
  },
  {
    key: "town-hold",
    name: "LOW TOWN DEFENSE",
    position: [-215, 0, 185],
    blurb: "Hold the low town crossroads until the relief column arrives.",
    briefing: [
      "Alpha holds the low town crossroads. Relief is inbound — keep them alive until it lands.",
      "It's tight down there. Danger close is real. Watch the alleys.",
    ],
  },
];

const HOLD_BASE_SECONDS = 75;

// Odd defend variants carry a VIP (MISSION_VARIETY §DEFEND variants): a
// non-combatant with the squad at the strongpoint — one more thing to keep
// alive, inside your blast radius, and losing them ends the mission.
const VIP_CALLSIGN = "PACKAGE";

export function buildDefendMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const sites = theater.defendSites;
  const site = sites[node.variant % sites.length]!;
  const vip = node.variant % 2 === 1;
  const holdSeconds = Math.round(HOLD_BASE_SECONDS + node.col * 12);
  const count = (n: number) => Math.max(1, Math.round(n * scaling.countMult));

  // Three assault anchors converge at 120° spacing; a threshold wave backs
  // them up. Spawns sit far enough out to honor the min-safe-distance rule.
  const ring = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3].map((angle, i) => ({
    id: `defend-ring-${i}`,
    source: "ridge",
    trigger: "immediate",
    count: count(4),
    origin: [
      site.position[0] + Math.cos(angle) * 210,
      0,
      site.position[2] + Math.sin(angle) * 210,
    ],
    spread: 40,
    posture: "assault",
    activateRadius: 4000,
    reinforcementEligible: true,
  }));

  const transformed: Record<string, unknown> = {
    id: `run-${site.key}${vip ? "-vip" : ""}`,
    name: vip ? `VIP HOLD: ${site.name.replace(" DEFENSE", "")}` : site.name,
    mapId: theater.mapId,
    squadMode: "hold",
    squadRoute: [
      { position: site.position, arrivalRadius: 30, label: "STRONGPOINT" },
    ],
    enemyAnchors: [
      ...ring,
      {
        id: "defend-late-wave",
        source: "vehicle",
        trigger: "threshold",
        enemiesAlive: 3,
        count: count(5),
        origin: [site.position[0] + 240, 0, site.position[2] - 160],
        spread: 44,
        posture: "assault",
        activateRadius: 4000,
        reinforcementEligible: true,
      },
    ],
    reinforcementBudget: Math.max(0, Math.round(8 * scaling.countMult) + scaling.budgetDelta),
    briefing: site.briefing,
    winObjectives: [
      { type: "HoldOut", seconds: holdSeconds, label: `HOLD ${holdSeconds}S UNTIL RELIEF` },
    ],
    failObjectives: [
      { type: "SquadSurvival", label: `KEEP ${scaling.minSurvivors}+ ALIVE`, minSurvivors: scaling.minSurvivors },
      ...(vip ? [{ type: "TrappedSurvival", label: `KEEP THE ${VIP_CALLSIGN} ALIVE` }] : []),
    ],
  };
  if (vip) {
    transformed.briefing = [...site.briefing,
      `The ${VIP_CALLSIGN} is with the squad and cannot fight. Every round you fire lands next to them.`];
  }

  const config = loadMission(transformed);
  config.squadSize = scaling.squadSize;
  if (vip) {
    config.trappedSquad = {
      position: [site.position[0] + 6, 0, site.position[2] + 6],
      count: 1,
      label: VIP_CALLSIGN,
      holdTogether: true,
    };
  }
  return config;
}

const DESTROY_WINDOW_SECONDS = 150;

export function buildDestroyMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const spec = theater.destroy;
  // Odd variants hit the COMMAND POST instead of the dump: a harder
  // garrison around softer targets, on a tighter window.
  const commandPost = node.variant % 2 === 1;
  const window = Math.max(
    commandPost ? 80 : 90,
    Math.round((DESTROY_WINDOW_SECONDS - (commandPost ? 25 : 0)) - node.col * 10),
  );
  const transformed: Record<string, unknown> = {
    id: commandPost ? `${spec.id}-cp` : spec.id,
    name: commandPost ? "FIRE MISSION: COMMAND POST" : spec.name,
    mapId: theater.mapId,
    squadMode: "hold",
    // No boots on the ground — the route node only seeds the orbit.
    squadRoute: [
      { position: spec.orbitPosition, arrivalRadius: 30, label: "TARGET AREA" },
    ],
    enemyAnchors: [
      {
        id: "destroy-garrison",
        source: "building",
        trigger: "immediate",
        count: Math.max(1, Math.round((commandPost ? 9 : 6) * scaling.countMult)),
        origin: spec.garrisonOrigin,
        spread: 46,
        posture: "garrison",
        activateRadius: 4000,
        reinforcementEligible: false,
      },
    ],
    reinforcementBudget: 0,
    briefing: commandPost
      ? [
        `No friendlies on the ground. A REDFOR command element is working out of ${spec.briefingPlace}.`,
        `Their signals trucks and security are packing NOW. Destroy the vehicles before they disperse. Window: ${window} seconds.`,
        "The staff will not stay to die with the radios — expect the garrison to fight while the trucks run.",
      ]
      : [
        `No friendlies on the ground. Pure fire mission — REDFOR is staging armor and fuel at ${spec.briefingPlace}.`,
        `Destroy every vehicle and gun before they disperse. Window: ${window} seconds.`,
        "No friendly fire risk tonight. Weapons free.",
      ],
    winObjectives: [
      { type: "DestroyPrimaries", label: "DESTROY ALL STAGED ARMOR + AA" },
    ],
    failObjectives: [
      { type: "TimeLimit", seconds: window, label: `WINDOW ${window}S` },
    ],
  };

  const config = loadMission(transformed);
  config.squadSize = 0;
  config.armorUnits = spec.armor.map((u) => ({ kind: u.kind, origin: [...u.origin] as Vec3 }));
  config.flakSites = spec.flak.map((f) => ({ kind: f.kind, origin: [...f.origin] as Vec3 }));
  // Deep-run dumps stage more armor
  if (node.col >= 2) config.armorUnits.push({ kind: "tank", origin: [...spec.deepTank] as Vec3 });
  return config;
}

export function buildAmbushMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const baseJson = theater.baseJson;
  if (!isRecord(baseJson)) throw new Error("ambush base mission JSON must be an object");
  const base = baseJson as { squadRoute?: Array<{ position: Vec3 }> };
  const route = (base.squadRoute ?? []).map((n) => n.position);
  if (route.length < 3) throw new Error("ambush: base route too short for a convoy path");
  // The convoy runs the valley road in reverse: staged past the low town,
  // driving hard for FOB KESTREL.
  const convoyPath = [...route].reverse();

  const transformed: Record<string, unknown> = {
    id: theater.ambush.id,
    name: theater.ambush.name,
    mapId: theater.mapId,
    squadMode: "hold",
    squadRoute: [
      { position: convoyPath[0]!, arrivalRadius: 30, label: "INTERCEPT POINT" },
    ],
    enemyAnchors: [],
    reinforcementBudget: 0,
    briefing: [
      `REDFOR column is rolling, driving for ${theater.ambush.destination}. No friendlies in the fight.`,
      "Kill the column before it arrives. Lead vehicle first stops nothing — they will drive around the wrecks.",
      "One leaker is acceptable. Two is a failed mission.",
    ],
    winObjectives: [
      { type: "DestroyPrimaries", label: "DESTROY THE CONVOY" },
    ],
    failObjectives: [
      { type: "ConvoyIntercept", maxEscapes: 1, label: "MAX 1 LEAKER" },
    ],
  };

  const config = loadMission(transformed);
  config.squadSize = 0;
  // Odd variants roll an ARMORED column (few, hard — the 105's fight);
  // even ones a SUPPLY column (soft, many — don't waste the big shells).
  const armored = node.variant % 2 === 1;
  if (armored) config.name = config.name.replace("AMBUSH:", "AMBUSH [ARMORED]:");
  const trucks = armored
    ? Math.max(2, Math.round(2 * scaling.countMult))
    : Math.max(3, Math.round(4 * scaling.countMult));
  const units: NonNullable<MissionConfig["armorUnits"]> = [];
  for (let i = 0; i < trucks; i++) {
    const stagger = i * 26;
    units.push({
      kind: "truck",
      origin: [convoyPath[0]![0] + stagger * 0.4, 0, convoyPath[0]![2] + stagger],
      path: convoyPath,
    });
  }
  const tanks = armored ? Math.max(2, Math.round(2 * scaling.countMult)) : node.col >= 2 ? 1 : 0;
  for (let i = 0; i < tanks; i++) {
    units.push({
      kind: "tank",
      origin: [convoyPath[0]![0] + 8, 0, convoyPath[0]![2] + trucks * 13 + i * 30],
      path: convoyPath,
    });
  }
  config.armorUnits = units;
  return config;
}

const DELAY_BASE_BUDGET = 6;

// DELAY (docs/design/MISSION_VARIETY.md): the squad holds a chokepoint and
// a REDFOR column drives straight at it. Unlike AMBUSH there are friendlies
// at the far end of every gun run — precision fires or dead friends. One
// vehicle arriving is a failed mission.
export function buildDelayMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const baseJson = theater.baseJson;
  if (!isRecord(baseJson)) throw new Error("delay base mission JSON must be an object");
  const base = baseJson as { squadRoute?: Array<{ position: Vec3; label?: string }> };
  const route = (base.squadRoute ?? []).map((n) => n.position);
  const spec = theater.delay;
  if (spec.chokepointIndex >= route.length - 1) throw new Error(`delay: chokepoint ${spec.chokepointIndex} too near route end`);
  const chokepoint = route[spec.chokepointIndex]!;
  // The column drives the route in reverse, ending AT the chokepoint
  const convoyPath = route.slice(spec.chokepointIndex).reverse();

  const transformed: Record<string, unknown> = {
    id: spec.id,
    name: spec.name,
    mapId: theater.mapId,
    squadMode: "hold",
    squadRoute: [
      { position: chokepoint, arrivalRadius: 30, label: "CHOKEPOINT" },
    ],
    enemyAnchors: [
      {
        id: "delay-screen",
        source: "ridge",
        trigger: "immediate",
        count: Math.max(2, Math.round(4 * scaling.countMult)),
        origin: [chokepoint[0] + 190, 0, chokepoint[2] - 130],
        spread: 42,
        posture: "assault",
        activateRadius: 4000,
        reinforcementEligible: true,
      },
    ],
    reinforcementBudget: Math.max(0, Math.round(DELAY_BASE_BUDGET * scaling.countMult) + scaling.budgetDelta),
    briefing: spec.briefing,
    winObjectives: [
      { type: "DestroyPrimaries", label: "STOP THE COLUMN" },
    ],
    failObjectives: [
      { type: "ConvoyIntercept", maxEscapes: 0, label: "NONE MAY PASS" },
      { type: "SquadSurvival", label: `KEEP ${scaling.minSurvivors}+ ALIVE`, minSurvivors: scaling.minSurvivors },
    ],
  };

  const config = loadMission(transformed);
  config.squadSize = scaling.squadSize;
  // Odd variants SPLIT the column (MISSION_VARIETY §DELAY variants): a
  // second axis comes in from the flank — two approach vectors, one gun.
  const split = node.variant % 2 === 1;
  if (split) {
    config.name = `${config.name} [SPLIT]`;
    config.briefing = [...(config.briefing ?? []),
      "S-2: the column has split. Two axes of advance converge on the chokepoint — cover both or lose it."];
  }
  const flankStart = theater.sead.orbitPosition;
  const flankPath: Vec3[] = [
    [...flankStart] as Vec3,
    [(flankStart[0] + chokepoint[0]) / 2 + 60, 0, (flankStart[2] + chokepoint[2]) / 2 - 45],
    [...chokepoint] as Vec3,
  ];
  const trucks = Math.max(3, Math.round(3 * scaling.countMult));
  const units: NonNullable<MissionConfig["armorUnits"]> = [];
  for (let i = 0; i < trucks; i++) {
    const stagger = i * 30;
    const onFlank = split && i % 2 === 1;
    const path = onFlank ? flankPath : convoyPath;
    units.push({
      kind: "truck",
      origin: [path[0]![0] + stagger * 0.4, 0, path[0]![2] + stagger],
      path,
    });
  }
  // Deep-run columns ride with armor escort
  if (node.col >= 2) {
    units.push({
      kind: "tank",
      origin: [convoyPath[0]![0], 0, convoyPath[0]![2] + trucks * 16],
      path: convoyPath,
    });
  }
  config.armorUnits = units;
  return config;
}

const SEAD_WINDOW_SECONDS = 170;

// SEAD: no squad, no free-fire comfort — every AA site in the sector must
// die before the transports arrive, and the guns shoot back at the thing
// hunting them. The aircraft-risk layer IS the mission.
export function buildSeadMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const spec = theater.sead;
  const window = Math.max(100, Math.round(SEAD_WINDOW_SECONDS - node.col * 10));
  const transformed: Record<string, unknown> = {
    id: spec.id,
    name: spec.name,
    mapId: theater.mapId,
    squadMode: "hold",
    squadRoute: [
      { position: spec.orbitPosition, arrivalRadius: 30, label: "SECTOR CENTER" },
    ],
    enemyAnchors: [
      {
        id: "sead-security",
        source: "structure",
        trigger: "immediate",
        count: Math.max(2, Math.round(4 * scaling.countMult)),
        origin: [spec.sites[0]!.origin[0], 0, spec.sites[0]!.origin[2]],
        spread: 40,
        posture: "garrison",
        activateRadius: 4000,
        reinforcementEligible: false,
      },
    ],
    reinforcementBudget: 0,
    briefing: [
      `No friendlies on the ground. The transports cross this sector in ${window} seconds and ${spec.briefingPlace} will eat them alive.`,
      "Silence every gun. They will shoot back at the thing hunting them — that's you.",
      "Watch your altitude and keep the flares warm. This is the mission the risk was built for.",
    ],
    winObjectives: [
      { type: "DestroyPrimaries", label: "SILENCE EVERY AA SITE" },
    ],
    failObjectives: [
      { type: "TimeLimit", seconds: window, label: `TRANSPORT WINDOW ${window}S` },
    ],
  };

  const config = loadMission(transformed);
  config.squadSize = 0;
  // Odd variants are [HIDDEN]: every MANPADS team is invisible until it
  // fires — the doc's "hidden MANPADS" SEAD. The guns you can see; the
  // missiles you learn about.
  const hiddenHunt = node.variant % 2 === 1;
  if (hiddenHunt) {
    config.name = `${config.name} [HIDDEN]`;
    config.briefing = [...(config.briefing ?? []),
      "S-2: the MANPADS teams are dug in and camouflaged. You will not see them until they launch. Flares warm."];
  }
  config.flakSites = spec.sites.map((f) => ({
    kind: f.kind,
    origin: [...f.origin] as Vec3,
    ...(hiddenHunt && f.kind === "manpads" ? { hidden: true } : {}),
  }));
  // Deep sectors field one more MANPADS team
  if (node.col >= 2) {
    const last = spec.sites[spec.sites.length - 1]!;
    config.flakSites.push({ kind: "manpads", origin: [last.origin[0] + 70, 0, last.origin[2] - 55], ...(hiddenHunt ? { hidden: true } : {}) });
  }
  return config;
}

// EXTRACTION UNDER FIRE (docs/design/MISSION_VARIETY.md): the squad opens
// PINNED — garrisons astride the lane, Stop/Go engaged. The player clears a
// path, sends them with H, and keeps the lane open. The "hold your breath"
// moment is the design: nothing moves until you say so.
export function buildExtractionMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const baseJson = theater.baseJson;
  if (!isRecord(baseJson)) throw new Error("extraction base mission JSON must be an object");
  const base = baseJson as { squadRoute?: Array<Record<string, unknown> & { position: Vec3 }> };
  const route = base.squadRoute ?? [];
  const spec = theater.extraction;
  if (spec.routeFrom >= route.length - 1) throw new Error(`extraction: routeFrom ${spec.routeFrom} too near route end`);
  const sliced = structuredClone(route.slice(spec.routeFrom));
  // The lane ends on arrival — the finale hold belongs to the finale
  const last = sliced[sliced.length - 1]!;
  delete last.holdTime;
  delete last.holdUntilClear;
  // The squad is pinned, not patrolling: waypoint holds would double-stall
  for (const wp of sliced) delete wp.holdUntilClear;
  const count = (n: number) => Math.max(1, Math.round(n * scaling.countMult));

  // Garrisons astride the lane between each pair of nodes — awake from the
  // start (this is what "pinned" means), offset off the corridor.
  const anchors: Record<string, unknown>[] = [];
  for (let i = 0; i < sliced.length - 1; i++) {
    const a = sliced[i]!.position;
    const b = sliced[i + 1]!.position;
    const mx = (a[0] + b[0]) / 2;
    const mz = (a[2] + b[2]) / 2;
    const side = i % 2 === 0 ? 1 : -1;
    anchors.push({
      id: `lane-garrison-${i}`,
      source: "building",
      trigger: "immediate",
      count: count(4),
      origin: [mx + side * 95, 0, mz - side * 70],
      spread: 42,
      posture: "garrison",
      activateRadius: 4000,
      reinforcementEligible: true,
    });
  }
  anchors.push({
    id: "lane-counterattack",
    source: "vehicle",
    trigger: "threshold",
    enemiesAlive: 3,
    // Deep-run lanes get a heavier counterattack once the squad is moving
    count: count(5 + node.col),
    origin: [last.position[0] + 130, 0, last.position[2] - 150],
    spread: 44,
    posture: "assault",
    activateRadius: 4000,
    reinforcementEligible: true,
  });

  const transformed: Record<string, unknown> = {
    id: spec.id,
    name: spec.name,
    mapId: theater.mapId,
    squadMode: "advance",
    squadRoute: sliced,
    enemyAnchors: anchors,
    reinforcementBudget: Math.max(0, Math.round(9 * scaling.countMult) + scaling.budgetDelta),
    briefing: [
      `The squad is pinned at ${spec.pinnedAt} — garrisons hold the lane on both sides and they cannot move.`,
      "They hold until YOU send them. Clear the lane, then hit H — and keep it clear while they run.",
      "Once they're moving, everything that's left will converge. Don't send them into it. Send them through it.",
    ],
    winObjectives: [
      { type: "SquadReachDestination", label: `REACH ${(last["label"] as string) ?? "EXTRACTION"}` },
    ],
    failObjectives: [
      { type: "SquadSurvival", label: `KEEP ${scaling.minSurvivors}+ ALIVE`, minSurvivors: scaling.minSurvivors },
    ],
  };

  const config = loadMission(transformed);
  config.squadSize = scaling.squadSize;
  config.squadStartHeld = true;
  return config;
}

// RESCUE (docs/design/MISSION_VARIETY.md): two friendly elements — the
// squad you brought and the one you're saving. The route runs THROUGH the
// trapped position to extraction; the siege is already underway when the
// mission opens. Either element lost before link-up is a failed mission,
// and the trapped one is inside your blast radius the whole approach.
export function buildRescueMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef = THEATERS["stone-wall"]): MissionConfig {
  const baseJson = theater.baseJson;
  if (!isRecord(baseJson)) throw new Error("rescue base mission JSON must be an object");
  const base = baseJson as { squadRoute?: Array<{ position: Vec3 }> };
  const route = (base.squadRoute ?? []).map((n) => n.position);
  if (route.length < 3) throw new Error("rescue: base route too short");
  const spec = theater.rescue;
  const start = route[0]!;
  const exfil = route[route.length - 1]!;
  const count = (n: number) => Math.max(1, Math.round(n * scaling.countMult));

  // The siege: two assault anchors already converging on the trapped
  // element, plus an ambush on the approach leg and a threshold reserve.
  const anchors: Record<string, unknown>[] = [
    {
      id: "siege-east",
      source: "ridge",
      trigger: "immediate",
      count: count(4),
      origin: [spec.position[0] + 185, 0, spec.position[2] - 120],
      spread: 42,
      posture: "assault",
      activateRadius: 4000,
      reinforcementEligible: true,
    },
    {
      id: "siege-west",
      source: "ridge",
      trigger: "immediate",
      count: count(4),
      origin: [spec.position[0] - 170, 0, spec.position[2] + 140],
      spread: 42,
      posture: "assault",
      activateRadius: 4000,
      reinforcementEligible: true,
    },
    {
      id: "approach-ambush",
      source: "building",
      trigger: "waypointReached",
      waypointIndex: 0,
      count: count(4),
      origin: [(start[0] + spec.position[0]) / 2 + 90, 0, (start[2] + spec.position[2]) / 2 - 70],
      spread: 44,
      posture: "ambush",
      activateRadius: 250,
      reinforcementEligible: true,
    },
    {
      id: "rescue-reserve",
      source: "vehicle",
      trigger: "threshold",
      enemiesAlive: 3,
      // Deep runs answer the rescue with a heavier reserve
      count: count(5 + node.col),
      origin: [exfil[0] + 140, 0, exfil[2] - 160],
      spread: 44,
      posture: "assault",
      activateRadius: 4000,
      reinforcementEligible: true,
    },
  ];

  const transformed: Record<string, unknown> = {
    id: spec.id,
    name: spec.name,
    mapId: theater.mapId,
    squadMode: "advance",
    squadRoute: [
      { position: start, arrivalRadius: 24, label: "STEP-OFF", corridorWidth: 30 },
      { position: spec.position, arrivalRadius: 28, label: "LINK-UP", corridorWidth: 30, callout: `${spec.callsign}, hold on — Alpha is coming to you.` },
      { position: exfil, arrivalRadius: 34, label: "EXTRACTION", corridorWidth: 44 },
    ],
    enemyAnchors: anchors,
    reinforcementBudget: Math.max(0, Math.round(9 * scaling.countMult) + scaling.budgetDelta),
    briefing: [
      `${spec.who} — callsign ${spec.callsign} — is pinned and under siege. The siege does not pause while Alpha walks.`,
      `Keep ${spec.callsign} alive from orbit until Alpha reaches them, then bring everyone out together.`,
      `${spec.callsign} is inside your blast radius the entire approach. A 105 that saves them can also finish them.`,
    ],
    winObjectives: [
      { type: "SquadReachDestination", label: "REACH EXTRACTION" },
    ],
    failObjectives: [
      { type: "SquadSurvival", label: `KEEP ${scaling.minSurvivors}+ ALIVE`, minSurvivors: scaling.minSurvivors },
      { type: "TrappedSurvival", label: `KEEP ${spec.callsign} ALIVE` },
    ],
  };

  const config = loadMission(transformed);
  config.squadSize = scaling.squadSize;
  config.trappedSquad = { position: [...spec.position] as Vec3, count: spec.count, label: spec.callsign };
  return config;
}

// Mission modifiers land AFTER the kind builder: storm and escalating are
// engine effects, aa-dense and heavy-armor are extra authored threats near
// the fight. At most one per node; the name carries the tag.
export function applyNodeModifier(config: MissionConfig, modifier: NodeModifier): MissionConfig {
  const route = config.squadRoute ?? [];
  const mid = route[Math.floor(route.length / 2)]?.position ?? route[0]?.position;
  switch (modifier) {
    case "storm": {
      config.storm = true;
      config.dispersionMult = 1.7;
      config.stormAltitudeCap = 250; // HIGH band (≥280) is gone
      config.name = `${config.name} — STORM`;
      config.briefing = [...(config.briefing ?? []),
        "WEATHER: storm cell over the objective. Ceiling 250 — the safe altitude band does not exist tonight — and your groups will wander."];
      break;
    }
    case "escalating": {
      config.reinforceIntervalMult = 0.55;
      config.reinforcementBudget = (config.reinforcementBudget ?? 10) + 4;
      config.name = `${config.name} — ESCALATING`;
      config.briefing = [...(config.briefing ?? []),
        "S-2: REDFOR reserves are committed and rolling. Reinforcements will not stop coming — win before the math does."];
      break;
    }
    case "aa-dense": {
      const sites = config.flakSites ?? [];
      if (sites.length > 0) {
        const a = sites[0]!;
        const b = sites[sites.length - 1]!;
        sites.push(
          { kind: "aa", origin: [a.origin[0] + 85, a.origin[1], a.origin[2] - 65] },
          { kind: "aa", origin: [b.origin[0] - 70, b.origin[1], b.origin[2] + 80] },
        );
      } else if (mid) {
        config.flakSites = [
          { kind: "aa", origin: [mid.x + 110, 0, mid.z - 80] },
          { kind: "aa", origin: [mid.x - 95, 0, mid.z + 95] },
        ];
      }
      config.name = `${config.name} — AA DENSE`;
      config.briefing = [...(config.briefing ?? []),
        "S-2: additional gun positions confirmed in sector. Altitude discipline or SEAD-as-you-go — pick one."];
      break;
    }
    case "heavy-armor": {
      if (mid) {
        config.armorUnits = [...(config.armorUnits ?? []),
          { kind: "tank", origin: [mid.x + 120, 0, mid.z - 90] }];
      }
      config.name = `${config.name} — HEAVY ARMOR`;
      config.briefing = [...(config.briefing ?? []),
        "S-2: armor attached to the defending force. The 105 earns its mount tonight."];
      break;
    }
  }
  return config;
}

// One entry point for the flow: node kind → the right builder.
export function buildNodeMission(node: RunNode, scaling: VariantScaling, theaterKey: TheaterKey = "stone-wall"): MissionConfig {
  const theater = THEATERS[theaterKey];
  const config = buildKindMission(node, scaling, theater);
  return node.modifier ? applyNodeModifier(config, node.modifier) : config;
}

function buildKindMission(node: RunNode, scaling: VariantScaling, theater: TheaterDef): MissionConfig {
  switch (node.kind) {
    case "defend":
      return buildDefendMission(node, scaling, theater);
    case "destroy":
      return buildDestroyMission(node, scaling, theater);
    case "ambush":
      return buildAmbushMission(node, scaling, theater);
    case "delay":
      return buildDelayMission(node, scaling, theater);
    case "sead":
      return buildSeadMission(node, scaling, theater);
    case "extraction":
      return buildExtractionMission(node, scaling, theater);
    case "rescue":
      return buildRescueMission(node, scaling, theater);
    default:
      return buildVariantMission(variantForNode(node, theater), node.kind, scaling, theater.baseJson);
  }
}

// Name + blurb for the run-map screen, for every node kind.
export function nodeDescriptor(node: RunNode, theaterKey: TheaterKey = "stone-wall"): { name: string; blurb: string } {
  const theater = THEATERS[theaterKey];
  switch (node.kind) {
    case "resupply":
      return { name: "+1 SQUAD", blurb: "Reinforce the squad" };
    case "defend": {
      const sites = theater.defendSites;
      const site = sites[node.variant % sites.length]!;
      if (node.variant % 2 === 1) {
        return {
          name: `VIP: ${site.name.replace(" DEFENSE", "")}`,
          blurb: `${site.blurb} A non-combatant is with them — one more life inside your blast radius.`,
        };
      }
      return { name: site.name, blurb: site.blurb };
    }
    case "destroy":
      if (node.variant % 2 === 1) {
        return { name: "COMMAND POST", blurb: "No squad. Kill the signals trucks before the staff scatters — tight window, hard garrison." };
      }
      return { name: theater.destroy.name.replace("FIRE MISSION: ", ""), blurb: "No squad. Destroy all staged armor and AA before the window closes." };
    case "ambush":
      if (node.variant % 2 === 1) {
        return { name: `${theater.ambush.name.replace("AMBUSH: ", "")} [ARMORED]`, blurb: "No squad. An armored column — few and hard. The 105 earns its mount. Max 1 leaker." };
      }
      return { name: theater.ambush.name.replace("AMBUSH: ", ""), blurb: `No squad. Kill the convoy before it reaches ${theater.ambush.destination}. Max 1 leaker.` };
    case "delay":
      if (node.variant % 2 === 1) {
        return { name: `${theater.delay.name} [SPLIT]`, blurb: "Alpha blocks the chokepoint. The column comes on TWO axes — cover both, none may pass." };
      }
      return { name: theater.delay.name, blurb: "Alpha blocks the chokepoint. A column is coming — none may pass, and Alpha is danger close." };
    case "sead":
      if (node.variant % 2 === 1) {
        return { name: `${theater.sead.name.replace("SILENCE: ", "")} [HIDDEN]`, blurb: "No squad. The MANPADS are camouflaged — you won't see them until they launch. Flares warm." };
      }
      return { name: theater.sead.name.replace("SILENCE: ", ""), blurb: "No squad. Silence every AA site before the transport window. The guns shoot back." };
    case "extraction":
      return { name: theater.extraction.name, blurb: "The squad is pinned. Clear the lane, send them with H, keep it open." };
    case "rescue":
      return { name: theater.rescue.name, blurb: `${theater.rescue.callsign} is pinned and under siege. Reach them, then bring everyone home.` };
    default: {
      const v = variantForNode(node, theater);
      return { name: v.name, blurb: v.blurb };
    }
  }
}

// ── Theaters (docs/design/THEME.md) ────────────────────────────────────────
// Each theater packages a base mission JSON plus the specs every node-kind
// builder needs. Operations deploy TO a theater; the run map fights it.

export type TheaterKey = RunTheater;

export type TheaterDef = {
  key: TheaterKey;
  codename: string; // "OPERATION STONE WALL"
  place: string; // menu subtitle
  mapId: MissionConfig["mapId"];
  baseJson: unknown;
  variants: VariantSpec[]; // last entry is the finale
  defendSites: DefendSite[];
  destroy: {
    id: string;
    name: string;
    orbitPosition: Vec3;
    briefingPlace: string;
    garrisonOrigin: Vec3;
    armor: Array<{ kind: "tank" | "truck"; origin: Vec3 }>;
    deepTank: Vec3;
    flak: Array<{ kind: "aa" | "manpads"; origin: Vec3 }>;
  };
  ambush: {
    id: string;
    name: string;
    destination: string; // what the convoy is driving toward (briefing)
  };
  // DELAY: the squad blocks a chokepoint; a column converges on it.
  delay: {
    id: string;
    name: string;
    chokepointIndex: number; // route node the squad holds
    briefing: string[];
  };
  // SEAD: squad-less — silence every AA site before the window closes.
  sead: {
    id: string;
    name: string;
    orbitPosition: Vec3;
    briefingPlace: string;
    sites: Array<{ kind: "aa" | "manpads"; origin: Vec3 }>;
  };
  // EXTRACTION UNDER FIRE: pinned at routeFrom, fight out to the route end.
  extraction: {
    id: string;
    name: string;
    routeFrom: number; // node the squad is pinned at
    pinnedAt: string; // briefing name for where they're stuck
  };
  // RESCUE: a trapped element under siege off the route. Reach them, then
  // bring everyone home. Both elements must live to the link-up.
  rescue: {
    id: string;
    name: string;
    callsign: string; // "RAVEN" — trapped members are RAVEN-1..N
    position: Vec3; // where they're pinned (off the main track)
    count: number;
    who: string; // briefing: who they are
  };
};

// IRON MARKET route indices (missions/iron-market-01.json):
// 0 STAGING LOT, 1 MARKET SOUTH, 2 MARKET STREET, 3 THE PLAZA,
// 4 CIVIC BLOCK, 5 EXTRACTION LOT. Rooftop guns use origin[1] = 22.
export const IRON_MARKET_VARIANTS: VariantSpec[] = [
  {
    key: "market-push",
    name: "MARKET PUSH",
    routeFrom: 0,
    routeTo: 2,
    blurb: "Move Delta from the staging lot up the market street.",
    briefing: [
      "Delta steps off from the south staging lot, up the market street.",
      "Ambush expected where the stalls thicken. Rooftop gun covers the street — respect it or erase it.",
    ],
    flakSites: [{ kind: "aa", origin: [-56, 22, -132] }],
  },
  {
    key: "plaza-cross",
    name: "PLAZA CROSSING",
    routeFrom: 2,
    routeTo: 4,
    blurb: "Cross the open plaza under the minaret and into the civic block.",
    briefing: [
      "The plaza is open ground and everyone knows it. A nest covers the crossing from the northeast.",
      "Get Delta across and into the civic block. Danger close the whole way.",
    ],
    armorUnits: [{ kind: "truck", origin: [420, 0, 130] }],
  },
  {
    key: "civic-exfil",
    name: "CIVIC EXFIL",
    routeFrom: 3,
    routeTo: 5,
    blurb: "Clear the civic block and hold the extraction lot approaches.",
    briefing: [
      "From the plaza, Delta pushes the civic block to the north extraction lot.",
      "A rooftop gun owns the lot approach and technicals stage beyond it. Break both.",
    ],
    flakSites: [{ kind: "aa", origin: [204, 22, 204] }],
    armorUnits: [{ kind: "truck", origin: [430, 0, 370] }],
  },
  {
    key: "iron-market-full",
    name: "IRON MARKET CORRIDOR",
    routeFrom: 0,
    routeTo: 5,
    blurb: "The full corridor: market, plaza, civic block, vehicle extraction.",
    briefing: [
      "The full corridor. Market street, the plaza, the civic block, vehicle extraction at the north lot.",
      "Both rooftop guns are up. Technicals will crash the extraction. Check fire — the streets are narrow.",
    ],
    flakSites: [
      { kind: "aa", origin: [-56, 22, -132] },
      { kind: "aa", origin: [204, 22, 204] },
    ],
    armorUnits: [
      { kind: "truck", origin: [420, 0, 130] },
      { kind: "truck", origin: [430, 0, 370] },
    ],
  },
];

const IRON_MARKET_DEFEND_SITES: DefendSite[] = [
  {
    key: "plaza-hold",
    name: "PLAZA DEFENSE",
    position: [30, 0, 20],
    blurb: "Delta holds the plaza. Militia converge down every street.",
    briefing: [
      "Delta is static on the plaza with the intel package. They cannot move — you cover every street mouth.",
      "Militia converge from the market, the civic block, and the west alleys. It's all danger close.",
    ],
  },
  {
    key: "staging-hold",
    name: "STAGING LOT DEFENSE",
    position: [-380, 0, -330],
    blurb: "Hold the staging lot until the vehicles arrive.",
    briefing: [
      "The extraction fell through. Delta holds the staging lot until backup vehicles arrive.",
      "Open approaches from the north and east — long sightlines, use them.",
    ],
  },
];

// GREEN HAMMER route indices (missions/green-hammer-01.json):
// 0 RIVER LANDING, 1 CANOPY TRACK, 2 THE FORD, 3 BANANA GROVE,
// 4 RIDGE TRAIL, 5 BORDER LZ.
export const GREEN_HAMMER_VARIANTS: VariantSpec[] = [
  {
    key: "ford-push",
    name: "FORD CROSSING",
    routeFrom: 0,
    routeTo: 2,
    blurb: "Move Alpha off the river landing and across the ford.",
    briefing: [
      "Alpha beaches at the river landing and pushes the canopy track to the ford.",
      "Watchers on the landing slope, a nest on the far bank. They will let the point man wade before they fire.",
    ],
    flakSites: [{ kind: "aa", origin: [-10, 0, 6] }],
  },
  {
    key: "grove-push",
    name: "GROVE PUSH",
    routeFrom: 2,
    routeTo: 4,
    blurb: "Clear the grove village and gain the ridge trail.",
    briefing: [
      "From the ford, Alpha pushes through the banana grove village to the ridge trail.",
      "The garrison holds the huts and a truck stages beyond the grove. Ambush country the whole way.",
    ],
    armorUnits: [{ kind: "truck", origin: [310, 0, 40] }],
  },
  {
    key: "border-run",
    name: "BORDER RUN",
    routeFrom: 3,
    routeTo: 5,
    blurb: "Over the ridge trail to the cleared strip on the border.",
    briefing: [
      "The last leg: ridge trail to the border strip. High ground both sides of the trail.",
      "A gun watches the strip and vehicles will rush the LZ. Break them before the bird is on final.",
    ],
    flakSites: [{ kind: "aa", origin: [424, 0, 378] }],
    armorUnits: [{ kind: "truck", origin: [430, 0, 352] }],
  },
  {
    key: "green-hammer-full",
    name: "GREEN HAMMER CORRIDOR",
    routeFrom: 0,
    routeTo: 5,
    blurb: "The full corridor: landing, ford, grove, ridge, border extraction.",
    briefing: [
      "The full route. River landing, the ford, the grove village, the ridge trail, extraction at the border strip.",
      "Everything in this jungle fights from ambush. Your thermal is the only warning Alpha gets.",
      "Hold the strip until the bird is out. Then come home.",
    ],
    flakSites: [
      { kind: "aa", origin: [-10, 0, 6] },
      { kind: "aa", origin: [424, 0, 378] },
    ],
    armorUnits: [
      { kind: "truck", origin: [310, 0, 40] },
      { kind: "truck", origin: [430, 0, 352] },
    ],
  },
];

const GREEN_HAMMER_DEFEND_SITES: DefendSite[] = [
  {
    key: "ford-hold",
    name: "FORD DEFENSE",
    position: [-80, 0, -60],
    blurb: "Alpha holds the ford. REDFOR converges through the canopy.",
    briefing: [
      "The boats are late. Alpha holds the ford with the officer — they cannot move.",
      "Infantry converge under the canopy from three sides. Your thermal sees them; Alpha won't until it's close.",
    ],
  },
  {
    key: "grove-hold",
    name: "GROVE DEFENSE",
    position: [60, 0, 60],
    blurb: "Hold the grove village until the relief element arrives.",
    briefing: [
      "Alpha is fortified in the grove village waiting on relief. The huts are cover, not protection.",
      "They will come out of the treeline in rushes. Break each rush before it reaches the huts.",
    ],
  },
];

// IRON CURTAIN route indices (missions/iron-curtain-01.json):
// 0 ASSEMBLY FARM, 1 TREELINE ALPHA, 2 THE TRENCHES, 3 GUARD POST,
// 4 THE WIRE, 5 CROSSING POINT. Warsaw Pact fights with armor: escort
// variants field a tank where other theaters send trucks.
export const IRON_CURTAIN_VARIANTS: VariantSpec[] = [
  {
    key: "treeline-push",
    name: "TREELINE PUSH",
    routeFrom: 0,
    routeTo: 2,
    blurb: "Move the asset from the farm along the treelines to the trench network.",
    briefing: [
      "The asset steps off from the assembly farm, following the hedgerows east.",
      "Border patrols work these treelines and the trench garrison is dug in at the far end.",
    ],
    flakSites: [{ kind: "aa", origin: [-20, 0, -110] }],
  },
  {
    key: "trench-crossing",
    name: "TRENCH CROSSING",
    routeFrom: 2,
    routeTo: 4,
    blurb: "Through the trench network and past the guard post to the wire.",
    briefing: [
      "The trenches were dug for a war that hasn't started. Tonight they're between the asset and the wire.",
      "Armor is parked at the guard post. Kill it before it wakes up — 25mm will not do it.",
    ],
    armorUnits: [{ kind: "tank", origin: [130, 0, 40] }],
  },
  {
    key: "wire-run",
    name: "WIRE RUN",
    routeFrom: 3,
    routeTo: 5,
    blurb: "Past the towers, over the strip, across the crossing point.",
    briefing: [
      "The last kilometer: the wire, the plowed strip, the crossing. Every tower can see it.",
      "Mobile AA covers the strip and a reaction force will roll when the alarm sounds. Be faster.",
    ],
    flakSites: [{ kind: "aa", origin: [300, 0, 452] }],
    armorUnits: [{ kind: "truck", origin: [438, 0, 348] }],
  },
  {
    key: "iron-curtain-full",
    name: "IRON CURTAIN CORRIDOR",
    routeFrom: 0,
    routeTo: 5,
    blurb: "The full corridor: farm, treelines, trenches, guard post, the wire, the crossing.",
    briefing: [
      "The whole run: from the assembly farm to a West German crossing point, through everything the border has.",
      "Trenches, a guard post with armor, mobile AA, towers on the strip. No moon. No second chances.",
      "If this goes loud enough, the war starts. Surgical, Specter.",
    ],
    flakSites: [
      { kind: "aa", origin: [-20, 0, -110] },
      { kind: "aa", origin: [300, 0, 452] },
    ],
    armorUnits: [
      { kind: "tank", origin: [130, 0, 40] },
      { kind: "truck", origin: [438, 0, 348] },
    ],
  },
];

const IRON_CURTAIN_DEFEND_SITES: DefendSite[] = [
  {
    key: "trench-hold",
    name: "TRENCH DEFENSE",
    position: [-80, 0, -50],
    blurb: "The asset holds the captured trench line. Border troops counterattack.",
    briefing: [
      "The exfil window slipped. The squad holds a stretch of captured trench with the asset until it reopens.",
      "Counterattacks will come across open fields — long sightlines for them and for you. Use yours first.",
    ],
  },
  {
    key: "farm-hold",
    name: "FARMSTEAD DEFENSE",
    position: [-380, 0, -330],
    blurb: "Hold the assembly farm until the extraction resets.",
    briefing: [
      "The crossing is blown. The squad falls back on the assembly farm and holds until a new route opens.",
      "The barns are cover, not protection, and border troops know exactly where the farm is.",
    ],
  },
];

// ARCTIC WOLF route indices (missions/arctic-wolf-01.json):
// 0 CUTOFF RIDGE, 1 PINE HOLLOW, 2 FROZEN LAKE, 3 THE SADDLE,
// 4 FJORD ROAD, 5 FJORD LZ. The densest AA of any theater — the doc's
// "sophisticated mobile AA" is the identity here.
export const ARCTIC_WOLF_VARIANTS: VariantSpec[] = [
  {
    key: "lake-crossing",
    name: "LAKE CROSSING",
    routeFrom: 0,
    routeTo: 2,
    blurb: "Bring the team off the ridge and across the frozen lake.",
    briefing: [
      "The team comes off the cutoff ridge through the pines to the lake — then across it.",
      "On the ice they are the only dark thing for a kilometer. A gun overwatches the far shore.",
    ],
    flakSites: [{ kind: "aa", origin: [-12, 0, -2] }],
  },
  {
    key: "saddle-push",
    name: "SADDLE PUSH",
    routeFrom: 2,
    routeTo: 4,
    blurb: "Off the ice, over the saddle, onto the fjord road.",
    briefing: [
      "From the lake shore the route climbs the saddle. Soviet armor holds the far slope.",
      "MANPADS teams ski these ridges. Keep flares warm and kill the tank before the climb.",
    ],
    flakSites: [{ kind: "manpads", origin: [150, 0, 90] }],
    armorUnits: [{ kind: "tank", origin: [128, 0, 34] }],
  },
  {
    key: "fjord-run",
    name: "FJORD RUN",
    routeFrom: 3,
    routeTo: 5,
    blurb: "Down the fjord road to the shore before the window closes.",
    briefing: [
      "The last leg: down the fjord road to the shore LZ. The boat is already moving.",
      "Coastal AA covers the road and a reaction force will race you to the shore. Win the race.",
    ],
    flakSites: [{ kind: "aa", origin: [420, 0, 372] }],
    armorUnits: [{ kind: "truck", origin: [436, 0, 350] }],
  },
  {
    key: "arctic-wolf-full",
    name: "ARCTIC WOLF CORRIDOR",
    routeFrom: 0,
    routeTo: 5,
    blurb: "The full corridor: ridge, pines, the lake crossing, the saddle, the fjord.",
    briefing: [
      "The whole route: off the ridge, across the ice, over the saddle, down to the fjord.",
      "Everything the sector has is awake — armor on the saddle, MANPADS in the pines, guns on the coast.",
      "The coldest airspace you've flown. Bring them home.",
    ],
    flakSites: [
      { kind: "aa", origin: [-12, 0, -2] },
      { kind: "manpads", origin: [150, 0, 90] },
      { kind: "aa", origin: [420, 0, 372] },
    ],
    armorUnits: [
      { kind: "tank", origin: [128, 0, 34] },
      { kind: "truck", origin: [436, 0, 350] },
    ],
  },
];

const ARCTIC_WOLF_DEFEND_SITES: DefendSite[] = [
  {
    key: "lake-hold",
    name: "LAKE PERIMETER",
    position: [-80, 0, -60],
    blurb: "The team holds the lake shore. Arctic infantry converge across the ice.",
    briefing: [
      "The team is pinned at the lake shore waiting on the weather. They cannot move.",
      "Infantry will come across the open ice and through the pines. The ice gives you everything — use it.",
    ],
  },
  {
    key: "saddle-hold",
    name: "SADDLE DEFENSE",
    position: [60, 0, 70],
    blurb: "Hold the saddle until the team's window reopens.",
    briefing: [
      "The team holds the saddle — the only pass between the lake and the fjord.",
      "If REDFOR retakes it, nobody reaches the shore. Waves will come up both slopes.",
    ],
  },
];

export const THEATERS: Record<TheaterKey, TheaterDef> = {
  "stone-wall": {
    key: "stone-wall",
    codename: "OPERATION STONE WALL",
    place: "Hindu Kush foothills, Afghanistan — 1989",
    mapId: "river-valley",
    baseJson: riverValleyData,
    variants: VALLEY_VARIANTS,
    defendSites: DEFEND_SITES,
    destroy: {
      id: "run-supply-dump",
      name: "FIRE MISSION: SUPPLY DUMP",
      orbitPosition: [-110, 0, 380],
      briefingPlace: "the north-lot supply dump",
      garrisonOrigin: [-120, 0, 360],
      armor: [
        { kind: "truck", origin: [-140, 0, 395] },
        { kind: "truck", origin: [-95, 0, 405] },
        { kind: "tank", origin: [-70, 0, 370] },
      ],
      deepTank: [-45, 0, 425],
      flak: [{ kind: "aa", origin: [-150, 0, 345] }],
    },
    ambush: {
      id: "run-convoy-ambush",
      name: "AMBUSH: RIVER ROAD CONVOY",
      destination: "FOB KESTREL",
    },
    delay: {
      id: "run-bridge-delay",
      name: "HOLD THE BRIDGE FORK",
      chokepointIndex: 3,
      briefing: [
        "A REDFOR column is driving the valley road for the bridge fork. Alpha blocks it at the overwatch.",
        "Nothing gets past them — one vehicle through and the low town is lost. Alpha is danger close to your every shot.",
      ],
    },
    sead: {
      id: "run-valley-sead",
      name: "SILENCE: VALLEY GUNS",
      orbitPosition: [-250, 0, 20],
      briefingPlace: "the valley gun network",
      sites: [
        { kind: "aa", origin: [-360, 0, -300] },
        { kind: "aa", origin: [-115, 0, 265] },
        { kind: "aa", origin: [-260, 0, -60] },
        { kind: "manpads", origin: [-160, 0, 55] },
        { kind: "manpads", origin: [-40, 0, 380] },
      ],
    },
    extraction: {
      id: "run-lowtown-exfil",
      name: "PINNED: LOW TOWN",
      routeFrom: 3,
      pinnedAt: "the bridge overwatch",
    },
    rescue: {
      id: "run-raven-rescue",
      name: "RESCUE: RAVEN 3-3",
      callsign: "RAVEN",
      position: [-330, 0, 130],
      count: 2,
      who: "A downed helicopter crew",
    },
  },
  "iron-market": {
    key: "iron-market",
    codename: "OPERATION IRON MARKET",
    place: "A city with no official name — 1989",
    mapId: "iron-market",
    baseJson: ironMarketData,
    variants: IRON_MARKET_VARIANTS,
    defendSites: IRON_MARKET_DEFEND_SITES,
    destroy: {
      id: "run-motor-pool",
      name: "FIRE MISSION: MOTOR POOL",
      orbitPosition: [420, 0, 150],
      briefingPlace: "the militia motor pool east of the civic block",
      garrisonOrigin: [400, 0, 120],
      armor: [
        { kind: "truck", origin: [440, 0, 170] },
        { kind: "truck", origin: [455, 0, 120] },
        { kind: "tank", origin: [415, 0, 90] },
      ],
      deepTank: [470, 0, 200],
      flak: [{ kind: "aa", origin: [204, 22, 204] }],
    },
    ambush: {
      id: "run-boulevard-ambush",
      name: "AMBUSH: BOULEVARD COLUMN",
      destination: "the Delta staging lot",
    },
    delay: {
      id: "run-plaza-delay",
      name: "PLAZA BLOCKING POSITION",
      chokepointIndex: 3,
      briefing: [
        "A militia column is driving the corridor in reverse, straight for the plaza. Delta blocks it there.",
        "The streets funnel everything into the square — and Delta is standing in it. Check fire. Nothing passes.",
      ],
    },
    sead: {
      id: "run-rooftop-sead",
      name: "SILENCE: ROOFTOP NET",
      orbitPosition: [30, 0, 20],
      briefingPlace: "the rooftop gun net",
      sites: [
        { kind: "aa", origin: [-56, 22, -132] },
        { kind: "aa", origin: [204, 22, 204] },
        { kind: "aa", origin: [80, 22, -60] },
        { kind: "manpads", origin: [420, 0, 130] },
        { kind: "manpads", origin: [-240, 0, 80] },
      ],
    },
    extraction: {
      id: "run-civic-exfil-uf",
      name: "PINNED: THE PLAZA",
      routeFrom: 3,
      pinnedAt: "the plaza",
    },
    rescue: {
      id: "run-vector-rescue",
      name: "RESCUE: VECTOR TEAM",
      callsign: "VECTOR",
      position: [150, 0, -50],
      count: 3,
      who: "A cut-off recon team",
    },
  },
  "green-hammer": {
    key: "green-hammer",
    codename: "OPERATION GREEN HAMMER",
    place: "Insurgent-held jungle, Central America — 1989",
    mapId: "green-hammer",
    baseJson: greenHammerData,
    variants: GREEN_HAMMER_VARIANTS,
    defendSites: GREEN_HAMMER_DEFEND_SITES,
    destroy: {
      id: "run-jungle-airstrip",
      name: "FIRE MISSION: JUNGLE AIRSTRIP",
      orbitPosition: [430, 0, -160],
      briefingPlace: "the airstrip cut into the canopy northeast of the river",
      armor: [
        { kind: "truck", origin: [452, 0, -186] },
        { kind: "truck", origin: [408, 0, -128] },
        { kind: "tank", origin: [462, 0, -132] },
      ],
      garrisonOrigin: [418, 0, -182],
      deepTank: [488, 0, -196],
      flak: [{ kind: "aa", origin: [396, 0, -156] }],
    },
    ambush: {
      id: "run-highway-column",
      name: "AMBUSH: JUNGLE HIGHWAY COLUMN",
      destination: "the river landing",
    },
    delay: {
      id: "run-ford-delay",
      name: "HOLD THE FORD",
      chokepointIndex: 2,
      briefing: [
        "An insurgent column is coming down the corridor for the ford — the only crossing for miles. Alpha holds the near bank.",
        "Vehicles that reach the water will cross it. None may. Alpha is in the splash zone of everything you fire.",
      ],
    },
    sead: {
      id: "run-river-sead",
      name: "SILENCE: RIVER GUNS",
      orbitPosition: [60, 0, 60],
      briefingPlace: "the guns hidden under the canopy",
      sites: [
        { kind: "aa", origin: [-10, 0, 6] },
        { kind: "aa", origin: [424, 0, 378] },
        { kind: "aa", origin: [396, 0, -156] },
        { kind: "manpads", origin: [-260, 0, 120] },
        { kind: "manpads", origin: [150, 0, 90] },
      ],
    },
    extraction: {
      id: "run-grove-exfil-uf",
      name: "PINNED: BANANA GROVE",
      routeFrom: 3,
      pinnedAt: "the grove village",
    },
    rescue: {
      id: "run-dustoff-rescue",
      name: "RESCUE: DUSTOFF 6",
      callsign: "DUSTOFF",
      position: [-150, 0, 60],
      count: 2,
      who: "A medevac crew down in the trees",
    },
  },
  "iron-curtain": {
    key: "iron-curtain",
    codename: "OPERATION IRON CURTAIN",
    place: "Inner German border — 1989",
    mapId: "iron-curtain",
    baseJson: ironCurtainData,
    variants: IRON_CURTAIN_VARIANTS,
    defendSites: IRON_CURTAIN_DEFEND_SITES,
    destroy: {
      id: "run-sa9-battery",
      name: "FIRE MISSION: SA-9 BATTERY",
      orbitPosition: [420, 0, -170],
      briefingPlace: "the SA-9 battery staging behind the treelines",
      garrisonOrigin: [406, 0, -190],
      armor: [
        { kind: "truck", origin: [446, 0, -196] },
        { kind: "truck", origin: [400, 0, -140] },
        { kind: "tank", origin: [458, 0, -144] },
      ],
      deepTank: [486, 0, -206],
      flak: [
        { kind: "aa", origin: [386, 0, -166] },
        { kind: "aa", origin: [452, 0, -230] },
      ],
    },
    ambush: {
      id: "run-armor-column",
      name: "AMBUSH: BORDER ARMOR COLUMN",
      destination: "the assembly farm",
    },
    delay: {
      id: "run-trench-delay",
      name: "HOLD THE TRENCH LINE",
      chokepointIndex: 2,
      briefing: [
        "A border reaction column is rolling down the corridor toward the trench network. The squad blocks it at the line.",
        "If armor crosses the trenches the extraction is over. Nothing passes — and your friendlies are dug in at the aim point.",
      ],
    },
    sead: {
      id: "run-sa9-sead",
      name: "SILENCE: SA-9 SECTOR",
      orbitPosition: [60, 0, 70],
      briefingPlace: "the SA-9 umbrella over the border",
      sites: [
        { kind: "aa", origin: [-20, 0, -110] },
        { kind: "aa", origin: [300, 0, 452] },
        { kind: "aa", origin: [386, 0, -166] },
        { kind: "manpads", origin: [452, 0, -230] },
        { kind: "manpads", origin: [140, 0, 240] },
      ],
    },
    extraction: {
      id: "run-wire-exfil-uf",
      name: "PINNED: GUARD POST",
      routeFrom: 3,
      pinnedAt: "the guard post rubble",
    },
    rescue: {
      id: "run-locksmith-rescue",
      name: "RESCUE: LOCKSMITH",
      callsign: "LOCKSMITH",
      position: [30, 0, -190],
      count: 3,
      who: "Escaped prisoners with the asset's documents",
    },
  },
  "arctic-wolf": {
    key: "arctic-wolf",
    codename: "OPERATION ARCTIC WOLF",
    place: "Northern Norway, behind Soviet lines — 1989",
    mapId: "arctic-wolf",
    baseJson: arcticWolfData,
    variants: ARCTIC_WOLF_VARIANTS,
    defendSites: ARCTIC_WOLF_DEFEND_SITES,
    destroy: {
      id: "run-coastal-radar",
      name: "FIRE MISSION: COASTAL RADAR",
      orbitPosition: [420, 0, -170],
      briefingPlace: "the coastal radar site on the headland",
      garrisonOrigin: [404, 0, -192],
      armor: [
        { kind: "truck", origin: [448, 0, -198] },
        { kind: "truck", origin: [398, 0, -138] },
        { kind: "tank", origin: [460, 0, -146] },
      ],
      deepTank: [488, 0, -208],
      flak: [
        { kind: "aa", origin: [384, 0, -168] },
        { kind: "manpads", origin: [452, 0, -232] },
      ],
    },
    ambush: {
      id: "run-kola-column",
      name: "AMBUSH: KOLA SUPPLY COLUMN",
      destination: "the cutoff ridge",
    },
    delay: {
      id: "run-saddle-delay",
      name: "HOLD THE SADDLE",
      chokepointIndex: 3,
      briefing: [
        "A Soviet column is climbing the fjord road for the saddle — the only pass. The team blocks it at the top.",
        "One vehicle over the saddle and the pocket closes. None may pass, and the team is on the crest of your gun-target line.",
      ],
    },
    sead: {
      id: "run-coastal-sead",
      name: "SILENCE: COASTAL BATTERIES",
      orbitPosition: [200, 0, 180],
      briefingPlace: "the coastal AA belt",
      sites: [
        { kind: "aa", origin: [-12, 0, -2] },
        { kind: "aa", origin: [420, 0, 372] },
        { kind: "aa", origin: [384, 0, -168] },
        { kind: "manpads", origin: [150, 0, 90] },
        { kind: "manpads", origin: [452, 0, -232] },
      ],
    },
    extraction: {
      id: "run-fjord-exfil-uf",
      name: "PINNED: THE SADDLE",
      routeFrom: 3,
      pinnedAt: "the saddle crest",
    },
    rescue: {
      id: "run-glacier-rescue",
      name: "RESCUE: GLACIER STATION",
      callsign: "GLACIER",
      position: [-170, 0, 70],
      count: 2,
      who: "A weather-station team overrun at dawn",
    },
  },
};
